import {FC, useCallback, useEffect, useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import SharedData from '../../common/SharedData'
import Loader from '../../common/Loader'
import VariantMultiOfferForm from '../../offers/match-by-barcode/VariantMultiOfferForm'
import {PendingVariantGroup} from '../add-bulk/components/SubmitButtonRow'
import {getPendingVariants, resolvePendingVariant} from './core/_requests'

/**
 * Post-job variant review for an async bulk ADD or UPDATE. The worker auto-handled
 * the clear matches and parked the ambiguous (duplicated-barcode) rows here. The
 * supplier resolves each via the same VariantMultiOfferForm; on save (onDone) we
 * mark that barcode resolved and drop its card. When the list empties the job is
 * no longer review_required.
 *
 * Each parked group carries an `action` flag from the backend: for an UPDATE
 * (`edit`) group the supplier owns the duplicated variants, so we pass
 * seedOwnedRows — the form seeds those rows with the sheet's values and applies
 * them via updateOffer to the variant(s) the supplier picks. ADD (`add`) groups
 * leave it false and create offers as before.
 */
const BulkJobReview: FC = () => {
  const {jobId} = useParams()
  const {currentUser, language} = SharedData()
  const {formatMessage} = useIntl()
  const navigate = useNavigate()

  const [groups, setGroups] = useState<PendingVariantGroup[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await getPendingVariants(Number(jobId), currentUser?.token, language)
      // The backend seed uses snake_case (supplier_sku); map to the camelCase
      // BulkOfferSeed the form expects.
      const list: PendingVariantGroup[] = (res.data?.data ?? []).map((g: any) => ({
        barcode: g.barcode,
        // Edit jobs tag each parked group so the form applies the sheet's values
        // to the owned variant (updateOffer) instead of creating a new offer.
        action: g.action === 'edit' ? 'edit' : 'add',
        variants: g.variants ?? [],
        seed: g.seed
          ? {
              price: g.seed.price,
              quantity: g.seed.quantity,
              supplierSku: g.seed.supplier_sku,
              color: g.seed.color,
              size: g.seed.size,
            }
          : undefined,
      }))
      setGroups(list)
    } catch {
      setGroups([])
    } finally {
      setLoading(false)
    }
  }, [jobId, currentUser?.token, language])

  useEffect(() => {
    void load()
  }, [load])

  const onResolved = async (barcode: string) => {
    try {
      await resolvePendingVariant(Number(jobId), barcode, currentUser?.token, language)
    } catch {
      /* clearing the parked flag is best-effort; the offer/variant is already saved */
    }
    setGroups((gs) => gs.filter((g) => g.barcode !== barcode))
  }

  return (
    <div className='card'>
      <div className='card-header align-items-center'>
        <h3 className='card-title'>
          {formatMessage({id: 'bulk_processing_results'}) || 'Resolve variants'}
        </h3>
        <Link to='/products/bulk-jobs' className='btn btn-sm btn-light'>
          ← {formatMessage({id: 'back'}) || 'Back'}
        </Link>
      </div>
      <div className='card-body position-relative'>
        {loading ? <Loader isFull /> : null}

        {!loading && groups.length === 0 ? (
          <div className='text-center text-muted py-10'>
            {formatMessage({id: 'bulk_all_processed'}) || 'All variants resolved.'}
          </div>
        ) : null}

        {groups.map((g, i) => (
          <div className='border rounded p-4 mb-4' key={g.barcode}>
            <div className='fw-bold mb-3'>
              {g.barcode}{' '}
              <span className='text-muted fw-normal'>
                ({i + 1}/{groups.length})
              </span>
            </div>
            <VariantMultiOfferForm
              barcode={g.barcode}
              variants={g.variants}
              seed={g.seed}
              seedOwnedRows={g.action === 'edit'}
              hideHeader
              onDone={() => onResolved(g.barcode)}
            />
          </div>
        ))}

        {!loading && groups.length === 0 ? (
          <div className='text-center'>
            <button
              type='button'
              className='btn btn-primary'
              onClick={() => navigate('/products/bulk-jobs')}
            >
              {formatMessage({id: 'finish_processing'}) || 'Done'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default BulkJobReview
